import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody } from 'reactstrap';
import UserSyncReportRecipients from '../Shared/UserSyncReportRecipients';
import GoogleUserExportStudent from './GoogleUserExportStudent';
import GoogleUserExportStaff from './GoogleUserExportStaff';
import { CoreGoogleUserExportConfiguration } from '../../../../../../util/GoogleUserExportTypes';
import { getModules } from '../../../../../../store/ModuleAction';
import { CoreModule } from '../../../../../../models/CoreModels';
import { RequestState } from '../../../../../../models/RequestState';
import { RootState } from '../../../../../../store/store';
import StatusCode from '../../../../../../util/StatusCode';
import { validateStudentConfig, validateStaffConfig } from './GoogleUserExportValidation';
import i18n from 'i18next';


export const validator = (configuration: CoreGoogleUserExportConfiguration) => {
    const validation: string[] = [];

    const studentConfig = configuration.studentConfiguration;
    const staffConfig = configuration.staffConfiguration;

    validateStudentConfig(studentConfig, validation);
    validateStaffConfig(staffConfig, validation);

    return validation;
}

export const warningMessages = (originalConfiguration: CoreGoogleUserExportConfiguration, updatedConfiguration: CoreGoogleUserExportConfiguration) => {
    const warnings: string[] = [];

    if (originalConfiguration.staffConfiguration?.enabled && !updatedConfiguration.staffConfiguration?.enabled) {
        warnings.push(i18n.t('googleUserExport.warning.staffConfigInactivate', { ns: 'configuration' }))
    }

    if (originalConfiguration.studentConfiguration?.enabled && !updatedConfiguration.studentConfiguration?.enabled) {
        warnings.push(i18n.t('googleUserExport.warning.studentConfigInactivate', { ns: 'configuration' }))
    }

    return warnings;
}

interface GoogleUserExportConfigurationProps {
    configuration: CoreGoogleUserExportConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreGoogleUserExportConfiguration>) => void
    onValidChange: (arg: boolean) => void 
}

const GoogleUserExportConfiguration = (props: GoogleUserExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const dispatch = useDispatch();

    const [open, setOpen] = useState<boolean>(true);

    const modules = useSelector<RootState, RequestState<CoreModule[]>>(state => state.module.modules);

    const validation = validator(props.configuration);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration]);

    useEffect(() => {
        getModules()(dispatch);
    }, [dispatch]);

    let importModuleOptions: { value: string, text: string }[] = [];
    if (modules && modules.code === StatusCode.COMPLETE) {
        importModuleOptions = modules.data
            .filter(x => x.moduleType === 1)
            .map(x => x.moduleTypeName)
            .filter((v, i, a) => a.indexOf(v) === i)
            .map(x => ({
                value: x,
                text: x,
            }));
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.GoogleUserExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>

                    <GoogleUserExportStudent
                        configuration={props.configuration.studentConfiguration}
                        disableInput={props.disabled}
                        validation={validation}
                        update={(name, value) => props.updatePart({ [name]: value })}
                        configurationType="studentConfiguration"
                        sourceSystemOptions={importModuleOptions}
                    />

                    <GoogleUserExportStaff
                        configuration={props.configuration.staffConfiguration}
                        disableInput={props.disabled}
                        validation={validation}
                        update={(name, value) => props.updatePart({ [name]: value })}
                        configurationType="staffConfiguration"
                        sourceSystemOptions={importModuleOptions}
                    />

                    <UserSyncReportRecipients
                        reportRecipients={props.configuration.reportRecipients}
                        disableInput={props.disabled}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                    />

                </CardBody>
            </Collapse>
        </Card>
    );
}

export default GoogleUserExportConfiguration;
